import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import {
  getTicket,
  addTicketMessage,
  resolveTicket,
  reopenTicket,
  closeTicket,
} from '../api/tickets';
import type { Ticket } from '../types/ticket';
import { getShot, getEpisode } from '../api';
import TicketMessageBubble from '../components/TicketMessageBubble';
import TicketTimelineEvent from '../components/TicketTimelineEvent';
import StatusBadge from '../components/StatusBadge';
import Breadcrumb from '../components/Breadcrumb';
import { useToast } from '../components/Toast';
import { formatDate } from '../utils/formatDate';
import type { User } from '../types';
import { legacyEpisodePath, projectEpisodePath } from '../utils/routes';
import { hasPermission } from '../utils/permissions';

const REOPEN_WINDOW_MS = 7 * 24 * 3600 * 1000;

type PendingAction = 'send' | 'resolve' | 'reopen' | 'close' | null;

function readCurrentUser(): User | null {
  const raw = localStorage.getItem('user');
  if (!raw) return null;
  try {
    return JSON.parse(raw) as User;
  } catch {
    return null;
  }
}

export default function TicketDetail() {
  const { t } = useTranslation('tickets');
  const { id } = useParams<{ id: string }>();
  const toast = useToast();
  const currentUser = readCurrentUser();
  const [tk, setTk] = useState<Ticket | null>(null);
  const [reply, setReply] = useState('');
  const [pendingAction, setPendingAction] = useState<PendingAction>(null);
  const [loading, setLoading] = useState(true);
  const [shotEpisode, setShotEpisode] = useState<{ id: string; projectId: string; name: string; index: number } | null>(null);

  const isBusy = pendingAction !== null;

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await getTicket(id);
        if (!cancelled) setTk(data);
      } catch (e) {
        if (!cancelled) {
          setTk(null);
          toast.error(t('ticketDetail.loadFailed'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, t, toast]);

  useEffect(() => {
    if (!tk || tk.relatedType !== 'shot' || !tk.relatedId) {
      setShotEpisode(null);
      return;
    }
    let cancelled = false;
    const shotId = tk.relatedId;
    (async () => {
      try {
        const shot = await getShot(shotId);
        if (cancelled || !shot.episodeId) return;
        const ep = await getEpisode(shot.episodeId);
        if (!cancelled) setShotEpisode({ id: ep.id, projectId: ep.projectId, name: ep.name, index: ep.index });
      } catch {
        // Leave chip hidden on lookup failure; project/shot chips still render.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tk]);

  const load = async (): Promise<void> => {
    if (!id) return;
    try {
      const data = await getTicket(id);
      setTk(data);
    } catch (e) {
      toast.error(t('ticketDetail.refreshFailed'));
    }
  };

  const containerStyle: React.CSSProperties = {
    maxWidth: 900,
    margin: '0 auto',
    padding: '2rem 1rem',
  };

  if (!currentUser) {
    return (
      <div className="container" style={containerStyle}>
        <div className="card" style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-3)' }}>
          {t('ticketDetail.pleaseLogin')}
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="container" style={containerStyle}>
        <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-3)' }}>{t('ticketDetail.loading')}</div>
      </div>
    );
  }

  if (!tk) {
    const isAdminView = hasPermission(currentUser, 'operations');
    return (
      <div className="container" style={containerStyle}>
        <Breadcrumb
          items={[
            { label: t('ticketDetail.breadcrumbHome'), to: '/projects' },
            isAdminView
              ? { label: t('ticketDetail.breadcrumbAdmin'), to: '/admin/tickets' }
              : { label: t('ticketDetail.breadcrumbMyTickets'), to: '/tickets' },
            { label: t('ticketDetail.breadcrumbTitle') },
          ]}
        />
        <div className="card" style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-3)' }}>
          {t('ticketDetail.notFound')}
        </div>
      </div>
    );
  }

  const ticketId = id;
  if (!ticketId) {
    return (
      <div className="container" style={containerStyle}>
        <div className="card" style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-3)' }}>
          {t('ticketDetail.missingId')}
        </div>
      </div>
    );
  }

  const isAuthor = currentUser.id === tk.userId;
  const isAdmin = hasPermission(currentUser, 'operations');
  const isAdminView = isAdmin && !isAuthor;
  const isClosed = tk.status === 'closed';
  const isResolvedOrClosed = tk.status === 'resolved' || tk.status === 'closed';

  const resolveTimestamp = tk.resolvedAt ?? tk.closedAt ?? null;
  const canReopen =
    isAuthor &&
    isResolvedOrClosed &&
    resolveTimestamp !== null &&
    Date.now() - new Date(resolveTimestamp).getTime() < REOPEN_WINDOW_MS;

  let snapshotPretty = '';
  if (isAdmin && tk.contextSnapshot) {
    try {
      snapshotPretty = JSON.stringify(JSON.parse(tk.contextSnapshot), null, 2);
    } catch {
      snapshotPretty = tk.contextSnapshot;
    }
  }

  const runAction = async (
    kind: 'resolve' | 'reopen' | 'close',
    call: () => Promise<unknown>,
    errorPrefix: string,
    successMsg: string,
  ): Promise<void> => {
    if (isBusy) return;
    setPendingAction(kind);
    try {
      await call();
      toast.success(successMsg);
      await load();
    } catch (e) {
      toast.error(errorPrefix + ': ' + (e as Error).message);
    } finally {
      setPendingAction(null);
    }
  };

  const onSend = async (): Promise<void> => {
    if (isBusy || !reply.trim()) return;
    setPendingAction('send');
    try {
      await addTicketMessage(ticketId, reply, []);
      setReply('');
      await load();
    } catch (e) {
      toast.error(t('ticketDetail.sendFailed', { error: (e as Error).message }));
    } finally {
      setPendingAction(null);
    }
  };

  const onResolve = async (): Promise<void> => {
    if (isBusy) return;
    if (isAdmin && !isAuthor && !reply.trim()) {
      toast.error(t('ticketDetail.resolveRequireReply'));
      return;
    }
    const content = isAdmin && !isAuthor ? reply.trim() : undefined;
    setPendingAction('resolve');
    try {
      await resolveTicket(ticketId, content);
      if (content) setReply('');
      toast.success(t('ticketDetail.resolveSuccess'));
      await load();
    } catch (e) {
      toast.error(t('ticketDetail.resolveFailed', { error: (e as Error).message }));
    } finally {
      setPendingAction(null);
    }
  };
  const onReopen = (): Promise<void> =>
    runAction('reopen', () => reopenTicket(ticketId), t('ticketDetail.reopenFailed'), t('ticketDetail.reopenSuccess'));
  const onClose = (): Promise<void> =>
    runAction('close', () => closeTicket(ticketId), t('ticketDetail.closeFailed'), t('ticketDetail.closeSuccess'));

  const messages = tk.messages ?? [];

  return (
    <div className="container" style={containerStyle}>
      <Breadcrumb
        items={[
          { label: t('ticketDetail.breadcrumbHome'), to: '/projects' },
          isAdminView
            ? { label: t('ticketDetail.breadcrumbAdmin'), to: '/admin/tickets' }
            : { label: t('ticketDetail.breadcrumbMyTickets'), to: '/tickets' },
          { label: tk.title || t('ticketDetail.breadcrumbTitle') },
        ]}
      />

      <div className="page-header" style={{ alignItems: 'flex-start' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
            <StatusBadge status={tk.status} label={t(`status.${tk.status}`)} />
            <h1 className="page-title" style={{ margin: 0, fontSize: '1.25rem' }}>
              {tk.title}
            </h1>
          </div>
          <div style={{ color: 'var(--text-3)', fontSize: '0.8rem', marginTop: '0.4rem' }}>
            {t(`category.${tk.category}`, { defaultValue: tk.category })} · {t('ticketDetail.submittedAt', { date: formatDate(tk.createdAt) })} · {t('ticketDetail.userLabel')}
            {tk.userName}
          </div>
          {(tk.projectId || tk.relatedId) && (
            <div
              style={{
                marginTop: '0.6rem',
                display: 'flex',
                gap: '0.4rem',
                flexWrap: 'wrap',
                alignItems: 'center',
              }}
            >
              {tk.projectId && (
                <Link
                  to={`/projects/${tk.projectId}`}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.3rem',
                    padding: '0.2rem 0.6rem',
                    background: 'var(--surface-2)',
                    border: '1px solid var(--border)',
                    borderRadius: 'var(--radius-sm)',
                    fontSize: '0.8rem',
                    color: 'var(--primary)',
                    textDecoration: 'none',
                  }}
                >
                  <span style={{ color: 'var(--text-3)' }}>{t('ticketDetail.projectChip')}</span>
                  <span>{tk.projectName || tk.projectId}</span>
                </Link>
              )}
              {tk.relatedType === 'episode' && tk.relatedId && (
                <Link
                  to={tk.projectId ? projectEpisodePath(tk.projectId, tk.relatedId) : legacyEpisodePath(tk.relatedId)}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.3rem',
                    padding: '0.2rem 0.6rem',
                    background: 'var(--surface-2)',
                    border: '1px solid var(--border)',
                    borderRadius: 'var(--radius-sm)',
                    fontSize: '0.8rem',
                    color: 'var(--primary)',
                    textDecoration: 'none',
                  }}
                >
                  <span style={{ color: 'var(--text-3)' }}>{t('ticketDetail.episodeChip')}</span>
                  <span>{tk.relatedName || tk.relatedId}</span>
                </Link>
              )}
              {tk.relatedType === 'shot' && shotEpisode && (
                <Link
                  to={projectEpisodePath(shotEpisode.projectId, shotEpisode.id)}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.3rem',
                    padding: '0.2rem 0.6rem',
                    background: 'var(--surface-2)',
                    border: '1px solid var(--border)',
                    borderRadius: 'var(--radius-sm)',
                    fontSize: '0.8rem',
                    color: 'var(--primary)',
                    textDecoration: 'none',
                  }}
                >
                  <span style={{ color: 'var(--text-3)' }}>{t('ticketDetail.episodeChip')}</span>
                  <span>
                    {shotEpisode.index ? t('ticketDetail.episodeDisplay', { index: shotEpisode.index }) : ''}
                    {shotEpisode.name ? ` ${shotEpisode.name}` : ''}
                  </span>
                </Link>
              )}
              {tk.relatedType === 'shot' && tk.relatedId && (
                shotEpisode ? (
                  <Link
                    to={projectEpisodePath(shotEpisode.projectId, shotEpisode.id, `#shot-${tk.relatedId}`)}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.3rem',
                      padding: '0.2rem 0.6rem',
                      background: 'var(--surface-2)',
                      border: '1px solid var(--border)',
                      borderRadius: 'var(--radius-sm)',
                      fontSize: '0.8rem',
                      color: 'var(--primary)',
                      textDecoration: 'none',
                    }}
                    title={t('ticketDetail.shotJumpTooltip')}
                  >
                    <span style={{ color: 'var(--text-3)' }}>{t('ticketDetail.shotChip')}</span>
                    <span>{tk.relatedName || tk.relatedId}</span>
                  </Link>
                ) : (
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.3rem',
                      padding: '0.2rem 0.6rem',
                      background: 'var(--surface-2)',
                      border: '1px solid var(--border)',
                      borderRadius: 'var(--radius-sm)',
                      fontSize: '0.8rem',
                      color: 'var(--text-2)',
                    }}
                    title={t('ticketDetail.episodeLoadingTooltip')}
                  >
                    <span style={{ color: 'var(--text-3)' }}>{t('ticketDetail.shotChip')}</span>
                    <span>{tk.relatedName || tk.relatedId}</span>
                  </span>
                )
              )}
            </div>
          )}
        </div>
      </div>

      {snapshotPretty && (
        <details className="card" style={{ padding: '0.75rem 1rem', marginBottom: '1rem' }}>
          <summary style={{ cursor: 'pointer', fontSize: '0.85rem', color: 'var(--text-2)' }}>
            {t('ticketDetail.contextSnapshot')}
          </summary>
          <pre
            style={{
              background: 'var(--surface-2)',
              padding: '0.75rem',
              borderRadius: 'var(--radius-sm)',
              fontSize: 12,
              overflow: 'auto',
              maxHeight: 300,
              marginTop: '0.5rem',
              border: '1px solid var(--border)',
            }}
          >
            {snapshotPretty}
          </pre>
        </details>
      )}

      <div
        className="card"
        style={{ padding: '1rem 1.25rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}
      >
        {messages.length === 0 ? (
          <div style={{ color: 'var(--text-3)', textAlign: 'center', padding: '1.5rem' }}>
            {t('ticketDetail.noMessages')}
          </div>
        ) : (
          messages.map((m) =>
            m.isSystemEvent ? (
              <TicketTimelineEvent key={m.id} message={m} />
            ) : (
              <TicketMessageBubble
                key={m.id}
                message={m}
                isMine={m.authorId === currentUser.id}
              />
            ),
          )
        )}
      </div>

      {!isClosed ? (
        <div style={{ marginTop: '1rem' }}>
          <textarea
            className="input"
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            rows={3}
            placeholder={t('ticketDetail.replyPlaceholder')}
            maxLength={5000}
            style={{ width: '100%', resize: 'vertical' }}
          />
          <div
            style={{
              marginTop: '0.75rem',
              display: 'flex',
              gap: '0.5rem',
              flexWrap: 'wrap',
              alignItems: 'center',
            }}
          >
            {canReopen && (
              <button className="btn btn-ghost" onClick={onReopen} disabled={isBusy}>
                {pendingAction === 'reopen' ? t('ticketDetail.processing') : t('ticketDetail.reopenBtn')}
              </button>
            )}
            {(isAuthor || isAdmin) && tk.status === 'resolved' && (
              <button
                className="btn btn-ghost"
                onClick={onClose}
                disabled={isBusy}
                style={{ color: 'var(--danger)' }}
              >
                {pendingAction === 'close' ? t('ticketDetail.processing') : t('ticketDetail.closeBtn')}
              </button>
            )}

            <div
              style={{
                marginLeft: 'auto',
                display: 'flex',
                gap: '0.5rem',
                flexWrap: 'wrap',
                alignItems: 'center',
              }}
            >
              {isAuthor && !isResolvedOrClosed && (
                <button className="btn btn-ghost" onClick={onResolve} disabled={isBusy}>
                  {pendingAction === 'resolve' ? t('ticketDetail.processing') : t('ticketDetail.selfResolveBtn')}
                </button>
              )}
              {isAdmin && !isAuthor && !isResolvedOrClosed && (
                <button
                  className="btn btn-ghost"
                  onClick={onResolve}
                  disabled={isBusy || !reply.trim()}
                  title={!reply.trim() ? t('ticketDetail.markResolvedHint') : undefined}
                >
                  {pendingAction === 'resolve' ? t('ticketDetail.processing') : t('ticketDetail.markResolvedBtn')}
                </button>
              )}
              <button
                className="btn btn-primary"
                onClick={onSend}
                disabled={isBusy || !reply.trim()}
              >
                {pendingAction === 'send' ? t('ticketDetail.sendingBtn') : t('ticketDetail.sendBtn')}
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div
          style={{
            marginTop: '1rem',
            padding: '0.85rem 1rem',
            background: 'var(--surface-2)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius)',
            color: 'var(--text-3)',
            fontSize: '0.85rem',
          }}
        >
          {t('ticketDetail.closedNoReply')}
          {canReopen && (
            <button
              className="btn btn-ghost"
              onClick={onReopen}
              disabled={isBusy}
              style={{ marginLeft: '0.75rem' }}
            >
              {pendingAction === 'reopen' ? t('ticketDetail.processing') : t('ticketDetail.reopenBtn')}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
